import { useState } from "react";
import { Landmark, ChevronDown, ChevronUp } from "lucide-react";

type Props = {
  eligibleSubtotal: number;
  total: number;
};

// Splits the order total into an EBT-payable portion (SNAP-eligible items
// from an approved seller) and the card-payable remainder. Renders nothing
// unless the cart actually has eligible items — see shared/snap.ts and the
// seller-level snapApproved flag that gates this. Preview only: no TPP/EBT
// rail is wired up yet, so the real charge still runs 100% through Stripe.
export function EbtPartitionPreview({ eligibleSubtotal, total }: Props) {
  const [partitioned, setPartitioned] = useState(false);
  if (eligibleSubtotal <= 0) return null;

  const cardRemaining = Math.max(0, total - eligibleSubtotal);

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm dark:border-emerald-800/40 dark:bg-emerald-950/30">
      <button
        type="button"
        onClick={() => setPartitioned((v) => !v)}
        className="flex w-full items-center justify-between gap-2 text-left"
        data-testid="button-ebt-partition-toggle"
      >
        <span className="flex items-center gap-2 font-medium text-emerald-800 dark:text-emerald-300">
          <Landmark className="h-4 w-4 shrink-0" />
          EBT SNAP can cover ${eligibleSubtotal.toFixed(2)} of this order
        </span>
        {partitioned ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-emerald-700 dark:text-emerald-400" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-emerald-700 dark:text-emerald-400" />
        )}
      </button>
      {partitioned && (
        <div className="mt-2 space-y-1.5 border-t border-emerald-200 pt-2 dark:border-emerald-800/40">
          <div className="flex justify-between text-emerald-800 dark:text-emerald-300">
            <span>Via EBT (SNAP-eligible items)</span>
            <span data-testid="text-ebt-portion">${eligibleSubtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Remaining, via card</span>
            <span data-testid="text-ebt-card-remainder">${cardRemaining.toFixed(2)}</span>
          </div>
          <p className="pt-1 text-[11px] text-muted-foreground">
            Preview — EBT payment isn't live yet. Once approved, this split happens automatically at checkout.
          </p>
        </div>
      )}
    </div>
  );
}
